import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Modal } from "flowbite-react";
import { isUserOwnerOfWorkspace, clog } from "../lib/utils";

function TopicCard({ topic, config, onAdd, onUpdate, onRemove }) {
  const [inputLimit, setInputLimit] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const isActive = !!config;

  const handleAdd = async () => {
    const limit = parseInt(inputLimit, 10);
    if (!limit || limit <= 0 || limit > topic.expectedSignals) return;
    setSaving(true);
    setCardError(null);
    try {
      await onAdd(topic.id, limit, topic.slug);
      setInputLimit("");
    } catch (err) {
      setCardError(err.message || "Failed to add signal");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleAdd();
  };

  const handleEditStart = () => {
    setEditValue(String(config.dailyLimit));
    setIsEditing(true);
  };

  const handleEditSave = async () => {
    const limit = parseInt(editValue, 10);
    if (!limit || limit <= 0 || limit > topic.expectedSignals) return;
    setSaving(true);
    setCardError(null);
    try {
      await onUpdate(topic.id, limit, topic.slug);
      setIsEditing(false);
    } catch (err) {
      setCardError(err.message || "Failed to update signal");
    } finally {
      setSaving(false);
    }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === "Enter") handleEditSave();
    if (e.key === "Escape") setIsEditing(false);
  };

  // Active card
  if (isActive) {
    const received = config.receivedToday;
    const limit = config.dailyLimit;
    const progress = Math.min((received / limit) * 100, 100);
    const isFull = received >= limit;

    return (
      <div
        className="relative rounded-[14px] p-4 border-2 transition-all duration-300"
        style={{
          borderColor: isFull ? "#10B981" : "#5E78F4",
          background: isFull ? "rgba(16,185,129,0.04)" : "rgba(94,120,244,0.04)",
        }}
      >
        {/* Active badge */}
        <div className="flex items-center justify-between mb-3">
          <span
            className="inline-flex items-center gap-1 text-[11px] font-[600] uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{
              background: isFull ? "rgba(16,185,129,0.12)" : "rgba(94,120,244,0.12)",
              color: isFull ? "#059669" : "#5E78F4",
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: isFull ? "#10B981" : "#5E78F4" }} />
            {isFull ? "Limit Reached" : "Active"}
          </span>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={handleEditStart}
              className="p-1 rounded-md hover:bg-[rgba(0,0,0,0.06)] transition-colors"
              title="Edit limit"
            >
              <svg className="w-3.5 h-3.5 text-[#6b7280]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
            <button
              onClick={() => setShowRemoveConfirm(true)}
              disabled={saving}
              className="p-1 rounded-md hover:bg-[rgba(239,68,68,0.1)] transition-colors disabled:opacity-40"
              title="Remove signal"
            >
              <svg className="w-3.5 h-3.5 text-[#6b7280] hover:text-[#DC2626]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Remove confirmation overlay */}
        {showRemoveConfirm && (
          <div className="absolute inset-0 rounded-[14px] bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center p-4 z-10">
            <p className="text-[14px] font-[600] text-[#1a1a1a] mb-1 text-center">Remove Signal</p>
            <p className="text-[12px] text-[#6b7280] mb-4 text-center">
              Are you sure you want to remove <strong>{topic.name}</strong>?
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowRemoveConfirm(false)}
                disabled={saving}
                className="px-4 py-1.5 rounded-[8px] border border-[#d1d5db] text-[#6b7280] text-[12px] font-[600] hover:bg-[#f9fafb] disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setSaving(true);
                  setCardError(null);
                  try {
                    await onRemove(topic.id, topic.slug);
                  } catch (err) {
                    setCardError(err.message || "Failed to remove signal");
                    setShowRemoveConfirm(false);
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                className="px-4 py-1.5 rounded-[8px] bg-[#EF4444] text-white text-[12px] font-[600] hover:bg-[#DC2626] disabled:opacity-40 transition-colors"
              >
                {saving ? "Removing..." : "Remove"}
              </button>
            </div>
            {cardError && (
              <span className="text-[11px] text-red-500 mt-2">{cardError}</span>
            )}
          </div>
        )}

        {/* Topic name */}
        <h3 className="text-[15px] font-[600] text-[#1a1a1a] mb-1">{topic.name}</h3>
        <p className="text-[12px] text-[#6b7280] mb-3">
          We usually receive <strong className="text-[#404040]">{topic.expectedSignals}</strong> signals a day
        </p>

        {/* Edit mode */}
        {isEditing ? (
          <div className="flex flex-col gap-1.5 mb-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleEditKeyDown}
                autoFocus
                min="1"
                max={topic.expectedSignals}
                className={`w-full px-3 py-1.5 rounded-[8px] border bg-white text-[13px] text-[#1a1a1a] focus:outline-none focus:ring-2 focus:border-transparent ${
                  parseInt(editValue, 10) > topic.expectedSignals
                    ? "border-red-500 focus:ring-red-500 text-red-600"
                    : "border-[#5E78F4] focus:ring-[#5E78F4]"
                }`}
              />
              <button
                onClick={handleEditSave}
                disabled={saving || !editValue || parseInt(editValue, 10) <= 0 || parseInt(editValue, 10) > topic.expectedSignals}
                className="shrink-0 px-3 py-1.5 rounded-[8px] bg-[#5E78F4] text-white text-[12px] font-[600] hover:bg-[#4a63d6] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="shrink-0 px-3 py-1.5 rounded-[8px] border border-[#d1d5db] text-[#6b7280] text-[12px] font-[600] hover:bg-[#f9fafb] transition-colors"
              >
                Cancel
              </button>
            </div>
            {parseInt(editValue, 10) > topic.expectedSignals && (
              <span className="text-[11px] text-red-500">Limit cannot exceed expected daily signals ({topic.expectedSignals})</span>
            )}
            {cardError && (
              <span className="text-[11px] text-red-500">{cardError}</span>
            )}
          </div>
        ) : (
          <>
            {/* Progress */}
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[13px] font-[600] text-[#1a1a1a]">
                {received} <span className="text-[#9ca3af] font-[400]">/</span> {limit}
              </span>
              <span className="text-[11px] text-[#6b7280]">received today</span>
            </div>
            <div className="h-2 w-full rounded-full bg-[#e5e7eb] overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{
                  width: `${progress}%`,
                  background: isFull
                    ? "#10B981"
                    : progress > 70
                    ? "#F59E0B"
                    : "linear-gradient(90deg, #5E78F4, #8B5CF6)",
                }}
              />
            </div>
          </>
        )}
      </div>
    );
  }

  // Inactive card
  const isInputOverLimit = parseInt(inputLimit, 10) > topic.expectedSignals;

  return (
    <div className="rounded-[14px] p-4 bg-white border border-[#e9ebf0] hover:border-[#c7d0f4] hover:shadow-sm transition-all duration-300">
      <h3 className="text-[15px] font-[600] text-[#1a1a1a] mb-1">{topic.name}</h3>
      <p className="text-[12px] text-[#6b7280] mb-4">
        We usually receive <strong className="text-[#404040]">{topic.expectedSignals}</strong> signals a day
      </p>

      {/* Add signal input */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <input
            type="number"
            placeholder="Set daily limit"
            value={inputLimit}
            onChange={(e) => setInputLimit(e.target.value)}
            onKeyDown={handleKeyDown}
            min="1"
            max={topic.expectedSignals}
            className={`w-full px-3 py-1.5 rounded-[8px] border bg-[#f9fafb] text-[13px] text-[#1a1a1a] placeholder-[#9ca3af] focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
              isInputOverLimit
                ? "border-red-500 focus:ring-red-500 bg-red-50"
                : "border-[#e5e7eb] focus:ring-[#5E78F4]"
            }`}
          />
          <button
            onClick={handleAdd}
            disabled={saving || !inputLimit || parseInt(inputLimit, 10) <= 0 || isInputOverLimit}
            className="shrink-0 px-4 py-1.5 rounded-[8px] text-[12px] font-[600] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed bg-gradient-to-r from-[#5E78F4] to-[#7C5CF5] text-white hover:shadow-md active:scale-[0.97]"
          >
            {saving ? "Adding..." : "Add Signal"}
          </button>
        </div>
        {isInputOverLimit && (
          <span className="text-[11px] text-red-500">Cannot exceed expected daily signals ({topic.expectedSignals})</span>
        )}
        {cardError && (
          <span className="text-[11px] text-red-500">{cardError}</span>
        )}
      </div>
    </div>
  );
}

// Skeleton loader for cards while API is loading
function TopicCardSkeleton() {
  return (
    <div className="rounded-[14px] p-4 bg-white border border-[#e9ebf0] animate-pulse">
      <div className="h-4 w-3/4 bg-gray-200 rounded mb-2" />
      <div className="h-3 w-1/2 bg-gray-200 rounded mb-4" />
      <div className="flex items-center gap-2">
        <div className="h-8 flex-1 bg-gray-200 rounded-[8px]" />
        <div className="h-8 w-24 bg-gray-200 rounded-[8px]" />
      </div>
    </div>
  );
}

// ─── How Signals Work Modal ──────────────────────────────────────────
const SIGNAL_SOURCES = [
  { icon: "💼", name: "LinkedIn Activity", desc: "Company posts, engagement & hiring trends" },
  { icon: "📋", name: "Job Boards", desc: "LinkedIn Jobs, Indeed, Greenhouse & more" },
  { icon: "⭐", name: "Review Platforms", desc: "G2 and Capterra review signals" },
  { icon: "💰", name: "Funding Data", desc: "Crunchbase funding rounds & investments" },
  { icon: "🔧", name: "Tech Stack Changes", desc: "Technology adoption & migration signals" },
  { icon: "📰", name: "News & Press", desc: "Press mentions, announcements & coverage" },
  { icon: "💬", name: "Community", desc: "Discussions across developer & industry forums" },
  { icon: "➕", name: "More Coming", desc: "New sources added regularly" },
];

function HowSignalsWorkModal({ open, onClose }) {
  return (
    <Modal show={open} size="3xl" onClose={onClose} dismissible>
      <Modal.Body className="p-0 rounded-[12px] overflow-hidden max-h-[85vh] flex flex-col">
        {/* Header band — sticky */}
        <div className="bg-gradient-to-r from-[#5E78F4] to-[#8B5CF6] px-8 py-6 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[22px] font-[700] text-white mb-1">How Signals Work</h2>
              <p className="text-[14px] text-white/80">Real-time buying intent, delivered to the right person</p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
            >
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-8 py-6 overflow-y-auto flex-1">
          {/* Intro */}
          <p className="text-[15px] text-[#404040] leading-relaxed mb-6">
            Signals track real-time buying intent across your selected categories. When a prospect shows interest
            in a topic you're monitoring, we identify the signal, match it to a person, and deliver it directly
            to your pipeline — so you can act while the intent is still fresh.
          </p>

          {/* 3 Differentiators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="rounded-[12px] border border-[#e9ebf0] p-4 hover:border-[#5E78F4] hover:shadow-sm transition-all">
              <div className="w-9 h-9 rounded-full bg-[#EEF1FE] flex items-center justify-center mb-3">
                <svg className="w-4.5 h-4.5 text-[#5E78F4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h4 className="text-[14px] font-[600] text-[#1a1a1a] mb-1">Real-Time Delivery</h4>
              <p className="text-[13px] text-[#6b7280] leading-relaxed">
                Signals are delivered the moment they're detected — no batch processing, no delays. Act on intent while it's still relevant.
              </p>
            </div>

            <div className="rounded-[12px] border border-[#e9ebf0] p-4 hover:border-[#5E78F4] hover:shadow-sm transition-all">
              <div className="w-9 h-9 rounded-full bg-[#EEF1FE] flex items-center justify-center mb-3">
                <svg className="w-4.5 h-4.5 text-[#5E78F4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h4 className="text-[14px] font-[600] text-[#1a1a1a] mb-1">Person-Level Attribution</h4>
              <p className="text-[13px] text-[#6b7280] leading-relaxed">
                We don't just tell you which company — we identify the specific person behind every signal, so your outreach is always targeted.
              </p>
            </div>

            <div className="rounded-[12px] border border-[#e9ebf0] p-4 hover:border-[#5E78F4] hover:shadow-sm transition-all">
              <div className="w-9 h-9 rounded-full bg-[#EEF1FE] flex items-center justify-center mb-3">
                <svg className="w-4.5 h-4.5 text-[#5E78F4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-[14px] font-[600] text-[#1a1a1a] mb-1">Multi-Source Coverage</h4>
              <p className="text-[13px] text-[#6b7280] leading-relaxed">
                Signals are aggregated from 7+ verified sources across social, hiring, reviews, funding, and news — with new sources added regularly.
              </p>
            </div>
          </div>

          {/* Sources grid */}
          <div className="mb-6">
            <h3 className="text-[15px] font-[600] text-[#1a1a1a] mb-3">Sources We Monitor</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {SIGNAL_SOURCES.map((source) => (
                <div
                  key={source.name}
                  className="rounded-[10px] bg-[#f9fafb] border border-[#f0f1f3] p-3 hover:bg-[#f3f4ff] transition-colors"
                >
                  <span className="text-[18px] mb-1.5 block">{source.icon}</span>
                  <p className="text-[13px] font-[600] text-[#1a1a1a] mb-0.5">{source.name}</p>
                  <p className="text-[11px] text-[#6b7280] leading-snug">{source.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* How it works steps */}
          <div className="rounded-[12px] bg-[#f9fafb] border border-[#f0f1f3] p-5">
            <h3 className="text-[15px] font-[600] text-[#1a1a1a] mb-3">Getting Started</h3>
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#5E78F4] text-white text-[12px] font-[700] flex items-center justify-center">1</span>
                <p className="text-[13px] text-[#404040] leading-relaxed"><strong>Choose your signals</strong> — Browse the catalog and select the topics that align with your ideal customer profile.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#5E78F4] text-white text-[12px] font-[700] flex items-center justify-center">2</span>
                <p className="text-[13px] text-[#404040] leading-relaxed"><strong>Set daily limits</strong> — Control how many leads you receive per day for each signal to match your team's capacity.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#5E78F4] text-white text-[12px] font-[700] flex items-center justify-center">3</span>
                <p className="text-[13px] text-[#404040] leading-relaxed"><strong>Receive real-time leads</strong> — We continuously monitor sources and deliver person-level leads the moment buying intent is detected.</p>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function IntentTopic() {
  const navigate = useNavigate();
  const isOwner = isUserOwnerOfWorkspace();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterState, setFilterState] = useState("all"); // all | active | inactive
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topics, setTopics] = useState([]);

  // Active topic configs: { [topicId]: { dailyLimit, receivedToday } }
  const [activeConfigs, setActiveConfigs] = useState({});

  // Fetch catalog from API
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios({
          url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/intent-topics/catalog`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            jwt_token: localStorage.getItem("login_token"),
          },
        });

        if (response.data.success) {
          // API returns: { success: true, data: { intent_catalog: { "Category Name": [...topics] } } }
          const intentCatalog = response.data.data?.intent_catalog || {};
          let flatTopics = [];

          Object.entries(intentCatalog).forEach(([categoryName, categoryTopics]) => {
            if (Array.isArray(categoryTopics)) {
              categoryTopics.forEach((topic) => {
                flatTopics.push({
                  id: topic.id,
                  name: topic.display_name,
                  slug: topic.topic_slug,
                  category: categoryName,
                  expectedSignals: topic.available_per_day || 0,
                });
              });
            }
          });

          setTopics(flatTopics);

          // Populate active configs from user_intent array
          const userIntents = response.data.data?.user_intent || [];
          const configs = {};
          userIntents.forEach((intent) => {
            const matchedTopic = flatTopics.find((t) => t.slug === intent.topicSlug);
            if (matchedTopic) {
              configs[matchedTopic.id] = {
                dailyLimit: intent.userDailyLimit,
                receivedToday: 0,
              };
            }
          });
          setActiveConfigs(configs);

          // Fetch daily received counts for active signals
          try {
            const receivedRes = await axios({
              url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/intent-topics/received`,
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                jwt_token: localStorage.getItem("login_token"),
              },
            });

            if (receivedRes.data.success && Array.isArray(receivedRes.data.data)) {
              setActiveConfigs((prev) => {
                const updated = { ...prev };
                receivedRes.data.data.forEach((item) => {
                  const matchedTopic = flatTopics.find((t) => t.slug === item.topic_slug);
                  if (matchedTopic && updated[matchedTopic.id]) {
                    updated[matchedTopic.id] = {
                      ...updated[matchedTopic.id],
                      receivedToday: item.total || 0,
                    };
                  }
                });
                return updated;
              });
            }
          } catch (receivedErr) {
            clog("Failed to fetch received counts:", receivedErr);
            // Non-critical — progress bars will show 0, page still works
          }
        } else {
          setError(response.data.message || "Failed to load signals catalog");
        }
      } catch (err) {
        clog(err);
        if (err?.response?.status === 401) {
          localStorage.removeItem("login_token");
          localStorage.removeItem("login_data");
          navigate("/");
          return;
        }
        setError(err?.response?.data?.message || err?.message || "Failed to load signals catalog");
      } finally {
        setLoading(false);
      }
    };

    fetchCatalog();
  }, [navigate]);

  const handleAdd = async (topicId, dailyLimit, topicSlug) => {
    const response = await axios({
      url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/intent-topics/update`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        jwt_token: localStorage.getItem("login_token"),
      },
      data: {
        intent_topics: {
          topicSlug: topicSlug,
          userDailyLimit: dailyLimit,
        },
      },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to add signal");
    }

    setActiveConfigs((prev) => ({
      ...prev,
      [topicId]: { dailyLimit, receivedToday: 0 },
    }));
  };

  const handleUpdate = async (topicId, newLimit, topicSlug) => {
    const response = await axios({
      url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/intent-topics/update`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        jwt_token: localStorage.getItem("login_token"),
      },
      data: {
        intent_topics: {
          topicSlug: topicSlug,
          userDailyLimit: newLimit,
        },
      },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to update signal");
    }

    setActiveConfigs((prev) => ({
      ...prev,
      [topicId]: { ...prev[topicId], dailyLimit: newLimit },
    }));
  };

  const handleRemove = async (topicId, topicSlug) => {
    const response = await axios({
      url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/intent-topics/delete`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        jwt_token: localStorage.getItem("login_token"),
      },
      data: {
        topicSlug: topicSlug,
      },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to remove signal");
    }

    setActiveConfigs((prev) => {
      const next = { ...prev };
      delete next[topicId];
      return next;
    });
  };

  // Filter + search
  const filteredTopics = useMemo(() => {
    return topics.filter((t) => {
      const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase());
      if (filterState === "active") return matchesSearch && !!activeConfigs[t.id];
      if (filterState === "inactive") return matchesSearch && !activeConfigs[t.id];
      return matchesSearch;
    });
  }, [searchQuery, filterState, activeConfigs, topics]);

  // Group by category
  const grouped = useMemo(() => {
    const map = {};
    filteredTopics.forEach((t) => {
      if (!map[t.category]) map[t.category] = [];
      map[t.category].push(t);
    });
    return map;
  }, [filteredTopics]);

  const activeCount = Object.keys(activeConfigs).length;

  return (
    <div className="px-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-[24px] font-[600] pb-2 text-[#1a1a1a]">
            Signals
          </h4>
          <p className="text-[16px] text-[#404040]">
            Track buying signals and set daily lead limits for each signal.{" "}
            <button
              onClick={() => setShowHowItWorks(true)}
              className="text-[#5E78F4] font-[500] hover:underline cursor-pointer bg-transparent border-none p-0 text-[16px] inline"
            >
              Learn how signals work →
            </button>
          </p>
        </div>
      </div>

      <HowSignalsWorkModal open={showHowItWorks} onClose={() => setShowHowItWorks(false)} />

      {/* Search + Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <svg
            className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search signals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-[12px] border border-[#e5e7eb] bg-white text-[14px] text-[#1a1a1a] placeholder-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#5E78F4] focus:border-transparent transition-all"
          />
        </div>

        {/* Filter tabs */}
        <div className="flex items-center rounded-[10px] border border-[#e5e7eb] bg-[#f9fafb] p-0.5">
          {[
            { key: "all", label: "All" },
            { key: "active", label: `Active (${activeCount})` },
            { key: "inactive", label: "Available" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilterState(tab.key)}
              className={`px-4 py-1.5 rounded-[8px] text-[13px] font-[500] transition-all duration-200 ${
                filterState === tab.key
                  ? "bg-white text-[#1a1a1a] shadow-sm"
                  : "text-[#6b7280] hover:text-[#1a1a1a]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {!loading && (
          <span className="text-[13px] text-[#6b7280] ml-auto">
            {filteredTopics.length} signal{filteredTopics.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div>
          {[1, 2].map((catIdx) => (
            <div key={catIdx} className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-5 w-48 bg-gray-200 rounded animate-pulse" />
                <div className="flex-1 h-px bg-[#e9ebf0]" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <TopicCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <svg
            className="w-16 h-16 text-[#EF4444] mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <p className="text-[16px] font-[600] text-[#EF4444]">Failed to load signals</p>
          <p className="text-[14px] text-[#6b7280] mt-1 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 rounded-[10px] bg-[#5E78F4] text-white text-[14px] font-[600] hover:bg-[#4a63d6] transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Category sections */}
      {!loading && !error && (
        <>
          {Object.keys(grouped).length > 0 ? (
            Object.entries(grouped).map(([category, categoryTopics]) => (
              <div key={category} className="mb-8">
                {/* Category header */}
                <div className="flex items-center gap-3 mb-4">
                  <h5 className="text-[16px] font-[600] text-[#1a1a1a]">{category}</h5>
                  <span className="text-[12px] text-[#9ca3af] font-[500]">
                    {categoryTopics.length} signal{categoryTopics.length !== 1 ? "s" : ""}
                  </span>
                  <div className="flex-1 h-px bg-[#e9ebf0]" />
                </div>

                {/* Cards grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {categoryTopics.map((topic) => (
                    <TopicCard
                      key={topic.id}
                      topic={topic}
                      config={activeConfigs[topic.id]}
                      onAdd={handleAdd}
                      onUpdate={handleUpdate}
                      onRemove={handleRemove}
                    />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <svg
                className="w-16 h-16 text-[#d1d5db] mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-[16px] font-[600] text-[#6b7280]">No signals found</p>
              <p className="text-[14px] text-[#9ca3af] mt-1">Try a different search term</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default IntentTopic;
